import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Plus, Smartphone } from "lucide-react";
import { toast } from "../lib/toast";
import type { DevicePublic } from "@smsbridge/shared";
import { Button } from "../components/ui/Button";
import { CopyButton } from "../components/ui/CopyButton";
import { EmptyState } from "../components/ui/EmptyState";
import { Modal } from "../components/ui/Modal";
import { Highlight } from "../components/ui/Highlight";
import { InlineEdit } from "../components/ui/InlineEdit";
import { PageHeader } from "../components/ui/PageHeader";
import { Pagination } from "../components/ui/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { Toolbar, ToolbarGrow } from "../components/ui/Toolbar";
import { RowAction, RowActions } from "../components/ui/RowAction";
import { StatusPill } from "../components/ui/StatusPill";
import { Table, type TableColumn } from "../components/ui/Table";
import { api, apiErrorMessage } from "../lib/api";
import { formatDateTime, formatRelative } from "../lib/format";
import { useClientTable } from "../lib/useClientTable";
import { useMinimumDuration } from "../lib/useMinimumDuration";

/** Mirrors DEVICE_ONLINE_WINDOW_MS on the server — a device is "online" if it has checked in
 * within the last three minutes. Computed here so the badge stays truthful between refetches. */
const ONLINE_WINDOW_MS = 3 * 60 * 1000;

function isOnline(device: DevicePublic): boolean {
  if (!device.isEnabled || !device.lastHeartbeatAt) return false;
  return Date.now() - new Date(device.lastHeartbeatAt).getTime() < ONLINE_WINDOW_MS;
}

interface PairingToken {
  token: string;
  expiresAt: string;
}

export function DevicesPage() {
  const queryClient = useQueryClient();
  const [pairingOpen, setPairingOpen] = useState(false);

  const devicesQuery = useQuery({
    queryKey: ["devices"],
    queryFn: async () => (await api.get<DevicePublic[]>("/devices")).data,
    // Heartbeats land every minute; without this the online badge would go stale on screen.
    refetchInterval: 30_000,
  });

  const devicesQueryLoading = useMinimumDuration(devicesQuery.isLoading);

  const pairingMutation = useMutation({
    mutationFn: async () =>
      (await api.post<PairingToken>("/devices/pairing-token", {})).data,
    onError: (err) => toast.error(apiErrorMessage(err, "Could not create a pairing token.")),
  });

  const removeMutation = useMutation({
    mutationFn: async (deviceId: string) => api.delete(`/devices/${deviceId}`),
    onSuccess: () => {
      toast.success("Device removed.");
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not remove the device.")),
  });

  function openPairing() {
    setPairingOpen(true);
    pairingMutation.mutate();
  }

  // Client-side: GET /devices takes no query parameters and returns every paired device.
  const table = useClientTable<DevicePublic>(devicesQuery.data, (row) => [
    row.name,
    row.model,
    row.manufacturer,
    ...row.simSlots.map((slot) => slot.carrier),
  ]);

  /** Patches just the nickname from the row. The device's own settings modal is untouched. */
  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      api.patch(`/devices/${id}`, { name }),
    onSuccess: () => {
      toast.success("Device renamed.");
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not rename the device.")),
  });

  /**
   * Points sending at a specific phone.
   *
   * Worth knowing what this actually controls: when a send does not name a device, the server
   * resolves one by looking for the default FIRST and returning it WITHOUT checking whether it
   * has been seen recently. Only if no default exists does it fall back to the freshest
   * heartbeat. So an offline default still wins, and messages queue against a phone that will
   * never collect them until the pickup timeout fails them.
   *
   * That bites hardest after re-pairing: pairing always creates a NEW device row (there is no
   * dedupe on hardware id), and the FIRST device an account paired is the one flagged default.
   * Reinstall the app and the live phone is the second row while the dead first row keeps the
   * flag. Until now the dashboard offered no way to see that, let alone change it.
   */
  const setDefaultMutation = useMutation({
    mutationFn: async (deviceId: string) =>
      api.patch(`/devices/${deviceId}`, { isDefault: true }),
    onSuccess: () => {
      toast.success("Default device updated.");
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not set the default device.")),
  });

  const columns: TableColumn<DevicePublic>[] = [
    {
      key: "name",
      header: "Device",
      render: (row) => (
        <div className="flex min-w-0 flex-col">
          <InlineEdit
            value={row.name}
            ariaLabel={`Name for ${row.model}`}
            placeholder="Add a name"
            onSave={async (next) => {
              // A device with no name is unidentifiable in a list, so an empty rename is a
              // no-op rather than a save.
              if (!next) return;
              await renameMutation.mutateAsync({ id: row._id, name: next });
            }}
          >
            <Highlight text={row.name} query={table.search} className="font-medium text-text" />
          </InlineEdit>
          <span className="text-xs text-text-muted">
            {row.manufacturer} {row.model} · Android {row.androidVersion}
            {row.isDefault && (
              // Two identical handsets are indistinguishable in this list after a re-pair, and which
              // one is default decides where unrouted sends actually go. Surfacing it is half the fix;
              // without it the user cannot tell there is anything to correct.
              <span className="ml-2 rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent">
                Default
              </span>
            )}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      render: (row) => <StatusPill status={isOnline(row) ? "ONLINE" : "OFFLINE"} />,
    },
    {
      key: "sim",
      header: "SIM",
      render: (row) => (
        <span className="text-text-muted">
          {row.simSlots.length > 0
            ? row.simSlots.map((s) => s.carrier).join(", ")
            : "No SIM reported"}
        </span>
      ),
    },
    {
      key: "battery",
      header: "Battery",
      width: "100px",
      align: "right",
      render: (row) => (
        <span className="tnum text-text-muted">
          {row.batteryLevel === null ? "—" : `${row.batteryLevel}%`}
        </span>
      ),
    },
    {
      key: "lastHeartbeatAt",
      header: "Last seen",
      width: "160px",
      render: (row) => (
        <span className="text-text-muted">{formatRelative(row.lastHeartbeatAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "170px",
      align: "right",
      render: (row) => (
        <RowActions>
          {!row.isDefault && (
            <RowAction
              disabled={
                setDefaultMutation.isPending && setDefaultMutation.variables === row._id
              }
              onClick={() => setDefaultMutation.mutate(row._id)}
            >
              Make default
            </RowAction>
          )}
          <RowAction
            tone="danger"
            disabled={removeMutation.isPending && removeMutation.variables === row._id}
            onClick={() => removeMutation.mutate(row._id)}
          >
            Remove
          </RowAction>
        </RowActions>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        sticky
        title="Devices"
        description="Android phones paired to your account. Each one can send and receive SMS on your behalf."
        actions={
          <Button leftIcon={<Plus className="size-4" aria-hidden />} onClick={openPairing}>
            Add device
          </Button>
        }
      />

      {devicesQuery.isError && (
        <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {apiErrorMessage(devicesQuery.error, "Could not load your devices.")}
        </p>
      )}

      <Toolbar sticky>
        <ToolbarGrow className="sm:max-w-xs">
          <SearchInput
            value={table.search}
            onChange={table.setSearch}
            placeholder="Search device, model or carrier…"
          />
        </ToolbarGrow>
      </Toolbar>
      <Table
        stickyFirstColumn
        stickyHeader
        columns={columns}
        data={table.rows}
        getRowId={(row) => row._id}
        loading={devicesQueryLoading}
        emptyState={
          table.search ? (
            <EmptyState
              icon={<Smartphone className="size-6" aria-hidden />}
              title={`No results for "${table.search}"`}
              description="Try a different name or carrier, or clear the search to see every device."
            />
          ) : (
            <EmptyState
              icon={<Smartphone className="size-6" aria-hidden />}
              title="No devices paired"
              description="Install the SMS Fort Android app and scan a pairing code to connect your first phone."
              action={<Button onClick={openPairing}>Add device</Button>}
            />
          )
        }
      />

      <Pagination
        mode="counted"
        page={table.page}
        pageSize={table.pageSize}
        total={table.total}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
      />

      <Modal
        open={pairingOpen}
        onClose={() => setPairingOpen(false)}
        title="Pair a new device"
        description="Open the SMS Fort app on your phone and scan this code. It expires in 5 minutes and works once."
      >
        <div className="flex flex-col items-center gap-4 py-2">
          {pairingMutation.isPending && (
            <Loader2 className="size-6 animate-spin text-accent" aria-label="Generating code" />
          )}

          {pairingMutation.isError && (
            <p role="alert" className="text-sm text-danger">
              {apiErrorMessage(pairingMutation.error, "Could not create a pairing token.")}
            </p>
          )}

          {pairingMutation.data && (
            <>
              {/* White quiet zone is not decorative — scanners need the light background to
                  find the code against this dark UI. */}
              <div className="rounded-card bg-white p-4">
                <QRCodeSVG value={pairingMutation.data.token} size={200} level="M" />
              </div>

              <div className="w-full">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-xs text-text-muted">Can&apos;t scan? Enter this code manually:</p>
                  {/* The token is 48 hex characters. Without this it has to be read off one
                      screen and typed into a phone by hand, which is where transcription
                      errors — and the "invalid code" support questions — come from. */}
                  <CopyButton value={pairingMutation.data.token} label="Copy pairing code" />
                </div>
                <code className="block break-all rounded-control border border-border bg-bg px-3 py-2 font-mono text-xs text-text">
                  {pairingMutation.data.token}
                </code>
              </div>

              <p className="text-xs text-text-muted">
                Expires {formatDateTime(pairingMutation.data.expiresAt)}
              </p>

              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setPairingOpen(false);
                  void queryClient.invalidateQueries({ queryKey: ["devices"] });
                }}
              >
                Done
              </Button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
